import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { Store } from '@ngxs/store';
import { PaginatedBidListService } from '../../../services/paginated-bid-list/paginated-bid-list.service';
import {
  Observable,
  Subject
} from 'rxjs';
import {
  distinctUntilChanged,
  map,
  pluck,
  takeUntil
} from 'rxjs/operators';
import { SetBidlistSort, VDPConfigurationState } from 'remarketing-angular-library';
import { TranslocoService } from '@jsverse/transloco';
import { DropdownSelectComponent } from 'remarketing-ui-components-lib';
import { CommonModule } from '@angular/common';

export interface SortGroupOption {
  name: string;
  value: string;
  selected: boolean;
  identifier?: string;
  sortOrder?: 'ASC' | 'DESC';
  sortBy?: string;
}

export interface SortGroup {
  header?: string;
  options: SortGroupOption[];
}

export type SelectedSortGroupOption = Pick<SortGroupOption, Exclude<keyof SortGroupOption, 'selected'>>;

@Component({
    selector: 'app-bidlist-sort-select',
    templateUrl: './bidlist-sort-select.component.html',
    styleUrls: ['./bidlist-sort-select.component.scss'],
    imports: [DropdownSelectComponent, CommonModule]
})
export class BidlistSortSelectComponent implements OnInit, OnDestroy {
  private readonly destroyed$ = new Subject();
  /**
   * Language-independent keys for the two ungrouped sorts. Display names are translated, so the
   * value is the only safe key; mirrors React slugs in packages/remote-buyers/src/domain/sortOptions.ts.
   * Never compare option names — translating a label must not break sorting.
   */
  private readonly SORT_VALUE_RESERVE_STATUS = 'reserve-status';
  private readonly SORT_VALUE_COMMENTS = 'comments';
  sortGroupOptions: SortGroup[];
  @Input() public selection: string;
  @Input() label: string;
  @Input() stacked = false;
  @Input() disabledFlag = false;
  @Output() public sortSelectionChangedEvent = new EventEmitter<string>();
  @ViewChild('searchSortSelect') searchSortSelect;
  sortOptions$: Observable<{
    selected: SelectedSortGroupOption;
    sortByLabel: string;
    options: SortGroup[];
  }>;

  constructor(
    private readonly store: Store,
    readonly paginatedBidList: PaginatedBidListService,
    private readonly translocoService: TranslocoService
  ) {}

  ngOnInit() {
    this.translocoService.langChanges$.pipe(takeUntil(this.destroyed$)).subscribe(_ => {
      this.getTranslations();
    });
  }

  getTranslations() {
    this.translocoService
      .selectTranslateObject('vehicles.bidListFilter.bidlistSortSelect')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.sortGroupOptions = [
          {
            options: [
              {
                name: translate.optionReserveStatus,
                value: this.SORT_VALUE_RESERVE_STATUS,
                sortOrder: null,
                sortBy: null,
                selected: true,
                identifier: 'sbd_none'
              }
            ]
          },
          {
            options: [
              {
                name: translate.optionComments,
                value: this.SORT_VALUE_COMMENTS,
                sortOrder: null,
                sortBy: 'comments',
                selected: false,
                identifier: 'sbd_comments'
              }
            ]
          },
          {
            header: translate.headerDateListed,
            options: [
              {
                name: translate.optionOldest,
                value: 'postingDate_ASC',
                sortOrder: 'ASC',
                sortBy: 'postingDate',
                selected: false,
                identifier: 'sbd_date_listed_oldest'
              },
              {
                name: translate.optionNewest,
                value: 'postingDate_DESC',
                sortOrder: 'DESC',
                sortBy: 'postingDate',
                selected: false,
                identifier: 'sbd_date_listed_newest'
              }
            ]
          },
          {
            header: translate.headerDistance,
            options: [
              {
                name: translate.optionClosest,
                value: 'distance_ASC',
                sortOrder: 'ASC',
                sortBy: 'distance',
                selected: false,
                identifier: 'sbd_distance_nearest'
              },
              {
                name: translate.optionFarthest,
                value: 'distance_DESC',
                sortOrder: 'DESC',
                sortBy: 'distance',
                selected: false,
                identifier: 'sbd_distance_farthest'
              }
            ]
          },
          {
            header: translate.headerEndTime,
            options: [
              {
                name: translate.optionEarliest,
                value: 'endTime_ASC',
                sortOrder: 'ASC',
                sortBy: 'endTime',
                selected: false,
                identifier: 'sbd_end_time_earliest'
              },
              {
                name: translate.optionLatest,
                value: 'endTime_DESC',
                sortOrder: 'DESC',
                sortBy: 'endTime',
                selected: false,
                identifier: 'sbd_end_time_latest'
              }
            ]
          },
          {
            header: translate.headerMake,
            options: [
              {
                name: translate.optionAToZ,
                value: 'make_ASC',
                sortOrder: 'ASC',
                sortBy: 'make',
                selected: false,
                identifier: 'sbd_make_a_to_z'
              },
              {
                name: translate.optionZToA,
                value: 'make_DESC',
                sortOrder: 'DESC',
                sortBy: 'make',
                selected: false,
                identifier: 'sbd_make_z_to_a'
              }
            ]
          },
          {
            header: translate.headerModel,
            options: [
              {
                name: translate.optionAToZ,
                value: 'model_ASC',
                sortOrder: 'ASC',
                sortBy: 'model',
                selected: false,
                identifier: 'sbd_model_a_to_z'
              },
              {
                name: translate.optionZToA,
                value: 'model_DESC',
                sortOrder: 'DESC',
                sortBy: 'model',
                selected: false,
                identifier: 'sbd_model_z_to_a'
              }
            ]
          },
          {
            header: translate.headerModelYear,
            options: [
              {
                name: translate.optionOldest,
                value: 'modelYear_ASC',
                sortOrder: 'ASC',
                sortBy: 'modelYear',
                selected: false,
                identifier: 'sbd_model_year_oldest'
              },
              {
                name: translate.optionNewest,
                value: 'modelYear_DESC',
                sortOrder: 'DESC',
                sortBy: 'modelYear',
                selected: false,
                identifier: 'sbd_mode_year_newest'
              }
            ]
          },
          {
            header: translate.headerOdometer,
            options: [
              {
                name: translate.optionLowest,
                value: 'odometer_ASC',
                sortOrder: 'ASC',
                sortBy: 'odometer',
                selected: false,
                identifier: 'sbd_odometer_lowest'
              },
              {
                name: translate.optionHighest,
                value: 'odometer_DESC',
                sortOrder: 'DESC',
                sortBy: 'odometer',
                selected: false,
                identifier: 'sbd_odometer_highest'
              }
            ]
          },
          {
            header: translate.headerSpreadToBook,
            options: [
              {
                name: translate.optionLowest,
                value: 'spreadToBook_ASC',
                sortOrder: 'ASC',
                sortBy: 'spreadToBook',
                selected: false,
                identifier: 'sbd_spread_to_book_lowest'
              },
              {
                name: translate.optionHighest,
                value: 'spreadToBook_DESC',
                sortOrder: 'DESC',
                sortBy: 'spreadToBook',
                selected: false,
                identifier: 'sbd_spread_to_book_highest'
              }
            ]
          }
        ];
        this.sortOptions$ = this.store.select(VDPConfigurationState.currentUserState).pipe(
          pluck('bidListSort'),
          distinctUntilChanged<any>((prev, cur) => prev.sortBy === cur.sortBy && prev.sortOrder === cur.sortOrder),
          map(activeSort => {
            // Fallback when nothing matches: the translated Reserve Status option, never an English literal
            let selected: SortGroupOption = this.sortGroupOptions[0].options[0];
            let header: string;
            // Set the currently selected option
            const sortGroupOptions = this.sortGroupOptions.map(group => {
              const options = group.options.map(option => {
                option.selected = this.isActiveSort(option, activeSort);
                if (option.selected) {
                  selected = option;
                  header = group.header;
                }
                return option;
              });
              return {
                ...group,
                options
              };
            });
            const sortByLabel = `Sort By: ${header ? `${header},` : ''} ${selected.name}`;
            return {
              selected,
              sortByLabel,
              options: sortGroupOptions
            };
          })
        );
      });
  }

  /**
   * Whether an option is the active sort. Compares sortBy/sortOrder only (null, undefined and ''
   * treated alike) so the checkmark survives language switches. Pairs are unique per option:
   * Reserve Status = (null,null), Comments = ('comments',null).
   * @param option menu option
   * @param activeSort NGXS bidListSort
   * @returns true when the option's sortBy/sortOrder pair matches the active sort
   */
  private isActiveSort(option: SortGroupOption, activeSort: { sortBy?: string; sortOrder?: string }): boolean {
    return (
      (option.sortBy || null) === (activeSort?.sortBy || null) &&
      (option.sortOrder || null) === (activeSort?.sortOrder || null)
    );
  }

  /**
   * Applies the clicked dropdown option's sort to NGXS + the paginated bid list. Keys off the
   * option's stable `data-value` only; `data-name` is translated text and must not drive logic.
   * @param option the clicked element from app-dropdown-select (its `dataset.value` is read)
   */
  public updateSort(option) {
    const value = option?.dataset?.value;
    // Header/checkmark clicks carry no value
    if (!value) {
      return;
    }
    // Ungrouped sorts must be matched before the generic split, or 'reserve-status' becomes a sortBy
    if (value === this.SORT_VALUE_RESERVE_STATUS) {
      this.applySort(null, null, null, null, null, null);
      return;
    }
    if (value === this.SORT_VALUE_COMMENTS) {
      this.applySort('comments', null, null, null, null, null);
      return;
    }
    const { bidListPagination, bidListSort } = this.store.selectSnapshot(VDPConfigurationState.currentUserState);
    const sortOption = value.split('_');
    const sortBy = sortOption[0];
    const sortOrder = sortOption[1];
    const addedByUuid = bidListSort.addedByUuid;
    const bidListStatus = bidListSort.bidListStatus;
    // eslint-disable-next-line prefer-const
    let { providerName, providerValue } = this.store.selectSnapshot(
      _state => _state.user.userProfile.userPreferences.defaultBookProvider
    );
    providerName = providerName.replace(/\s+/g, '');
    this.applySort(sortBy, sortOrder, providerName, providerValue, addedByUuid, bidListStatus);
  }

  /**
   * Applies a sort to NGXS and the paginated bid list together so the two can't drift apart.
   * SetBidlistSort takes (sortBy, sortOrder) but setSort/updateSortState take (sortOrder, sortBy);
   * the swap lives here only.
   * @param sortBy field to sort by, or null for no sort
   * @param sortOrder ASC/DESC, or null
   * @param providerName book provider name, spaces stripped
   * @param providerValue book provider value
   * @param addedByUuid added-by filter carried through from the current sort
   * @param bidListStatus status filter carried through from the current sort
   */
  private applySort(
    sortBy: string,
    sortOrder: string,
    providerName: string,
    providerValue: string,
    addedByUuid: string,
    bidListStatus: string
  ) {
    this.store.dispatch(
      new SetBidlistSort(sortBy, sortOrder, providerName, providerValue, addedByUuid, bidListStatus)
    );
    this.paginatedBidList.setSort(sortOrder, sortBy, providerName, providerValue, addedByUuid, bidListStatus);
    this.paginatedBidList.updateSortState(
      sortOrder,
      sortBy,
      providerName,
      providerValue,
      addedByUuid,
      bidListStatus
    );
  }

  sortByFocus() {
    this.focusSelectSortBy(this.searchSortSelect.nativeElement.querySelector('#select-sort-by .dropdown-item'));
  }

  focusSelectSortBy(sortThenByHeading) {
    sortThenByHeading.focus();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
